import type { DealStage } from "../types";
import type { ConfigurationContextValue } from "./ConfigurationContext";
import { CUSTOMER_COLORS } from "../companies/customerColors";
import { STANDARD_FIT_CONFIG } from "@shared/fit.ts";

export const defaultDarkModeLogo = "./logos/mimin.svg";
export const defaultLightModeLogo = "./logos/mimin.svg";

export const defaultCurrency = "NOK";

export const defaultTitle = "Mimin";

// Byråets eget bedriftsnavn (Kontoinnstillinger). Tomt som standard; "Min
// bedrift"-raden faller tilbake til etiketten "Min bedrift" til dette settes.
export const defaultCompanyName = "";

// Norske bransjer tilpasset små byråer og SMB-kunder. Verdiene er stabile
// identifikatorer; labelene vises i grensesnittet.
export const defaultCompanySectors = [
  { value: "bygg-anlegg", label: "Bygg og anlegg" },
  { value: "handel", label: "Handel og varehandel" },
  { value: "helse-omsorg", label: "Helse og omsorg" },
  { value: "it-teknologi", label: "IT og teknologi" },
  { value: "industri-produksjon", label: "Industri og produksjon" },
  { value: "media-kommunikasjon", label: "Media og kommunikasjon" },
  { value: "finans-forsikring", label: "Finans og forsikring" },
  { value: "eiendom", label: "Eiendom" },
  { value: "reiseliv-servering", label: "Reiseliv og servering" },
  { value: "transport-logistikk", label: "Transport og logistikk" },
  { value: "utdanning", label: "Utdanning" },
  { value: "offentlig-sektor", label: "Offentlig sektor" },
  { value: "annet", label: "Annet" },
];

// Standardstadiene i pipelinen. Verdiene holdes stabile (lagres på
// deals.stage); kun labelene vises. I Supabase ligger stadiene i tabellen
// deal_stages og redigeres under Innstillinger → Pipeline. Listen her er
// reserve før den er lastet, og demodata. probability er 0–1.
// Vunnet, Tapt og Diskvalifisert er status på muligheten, ikke stadier.
export const defaultDealStages: DealStage[] = [
  {
    value: "lead",
    label: "Lead inn",
    position: 0,
    is_inbox: true,
    probability: 0.1,
    rot_after_workdays: 1,
    required_fields: ["contact", "source"],
    next_step_title: "Ring eller svar på henvendelsen",
    next_step_type: "call",
    next_step_offset_workdays: 0,
  },
  {
    value: "opportunity",
    label: "Kvalifisert",
    position: 1,
    is_inbox: false,
    probability: 0.2,
    rot_after_workdays: 5,
    required_fields: ["service", "contact", "budget", "next_step"],
    next_step_title: "Avtal møte",
    next_step_type: "call",
    next_step_offset_workdays: 2,
  },
  {
    value: "meeting",
    label: "Møte/befaring",
    position: 2,
    is_inbox: false,
    probability: 0.4,
    rot_after_workdays: 7,
    required_fields: ["meeting_date"],
    next_step_title: "Skriv og send tilbud",
    next_step_type: "task",
    next_step_offset_workdays: 3,
  },
  {
    value: "proposal-sent",
    label: "Tilbud sendt",
    position: 3,
    is_inbox: false,
    probability: 0.6,
    rot_after_workdays: 7,
    required_fields: ["offer_file", "value", "offer_valid_until"],
    next_step_title: "Følg opp tilbud",
    next_step_type: "call",
    next_step_offset_workdays: 4,
  },
  {
    value: "in-negociation",
    label: "Forhandling",
    position: 4,
    is_inbox: false,
    probability: 0.8,
    rot_after_workdays: 10,
    required_fields: [],
    next_step_title: "Avklar og få aksept",
    next_step_type: "call",
    next_step_offset_workdays: 3,
  },
];

export const defaultDealCategories = [
  { value: "other", label: "Annet" },
  { value: "copywriting", label: "Tekstforfatting" },
  { value: "print-project", label: "Trykksak" },
  { value: "ui-design", label: "UI-design" },
  { value: "website-design", label: "Nettsidedesign" },
];

export const defaultNoteStatuses = [
  { value: "cold", label: "Kald", color: "#7dbde8" },
  { value: "warm", label: "Varm", color: "#e8cb7d" },
  { value: "hot", label: "Het", color: "#e88b7d" },
  { value: "in-contract", label: "I kontrakt", color: "#a4e87d" },
];

// Standard prosjektstatuser (board-kolonner). Seedes på nye prosjekter.
export const defaultProjectStatuses = [
  { value: "todo", label: "Å gjøre", color: "#6b7280" },
  { value: "doing", label: "Pågår", color: "#3b82f6" },
  { value: "done", label: "Ferdig", color: "#10b981" },
];

// Standard fargepalett en kunde kan tildeles. Kan endres under Innstillinger.
export const defaultCompanyColors = CUSTOMER_COLORS.map((color) => ({ color }));

export const defaultTaskTypes = [
  { value: "none", label: "Ingen" },
  { value: "email", label: "E-post" },
  { value: "demo", label: "Demo" },
  { value: "lunch", label: "Lunsj" },
  { value: "meeting", label: "Møte" },
  { value: "follow-up", label: "Oppfølging" },
  { value: "thank-you", label: "Takk" },
  { value: "ship", label: "Levering" },
  { value: "call", label: "Telefon" },
];

export const defaultTimeBillableByDefault = false;

// Digitelle-fit for prospektering (P4). Standarden er bevisst tom for bransje,
// geografi og plattform: modellen skal ikke begynne å rangere om av seg selv
// den dagen koden deployes. Fyll den ut i Innstillinger → Prospektering.
export const defaultProspektFit = STANDARD_FIT_CONFIG;

export const defaultConfiguration: ConfigurationContextValue = {
  companySectors: defaultCompanySectors,
  companyColors: defaultCompanyColors,
  companyName: defaultCompanyName,
  currency: defaultCurrency,
  timeBillableByDefault: defaultTimeBillableByDefault,
  dealCategories: defaultDealCategories,
  dealStages: defaultDealStages,
  noteStatuses: defaultNoteStatuses,
  projectStatuses: defaultProjectStatuses,
  taskTypes: defaultTaskTypes,
  title: defaultTitle,
  darkModeLogo: defaultDarkModeLogo,
  lightModeLogo: defaultLightModeLogo,
  prospektFit: defaultProspektFit,
};
